import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { employeeAPI, flagsAPI } from '../../services/api';
import { toast } from 'sonner';
import { Building, Calendar, Star, ChevronLeft, Loader2, Flag, CheckCircle, AlertCircle, Info } from 'lucide-react';

const RecordReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [flagField, setFlagField] = useState(null);
  const [flagMessage, setFlagMessage] = useState('');
  const [flaggedFields, setFlaggedFields] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const res = await employeeAPI.getRecord(id);
      setRecord(res.data);
      // Build flagged fields map
      const flagged = {};
      res.data.flags?.forEach(f => {
        flagged[f.field_name] = f;
      });
      setFlaggedFields(flagged);
    } catch (error) {
      console.error('Error fetching record:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async () => {
    if (!flagMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      await flagsAPI.create({
        record_id: id,
        field_name: flagField,
        message: flagMessage
      });
      toast.success('Flag submitted');
      setFlagField(null);
      setFlagMessage('');
      fetchRecord();
    } catch (error) {
      toast.error('Failed to submit flag');
    }
  };

  const handleAcknowledge = async () => {
    const hasOpenFlags = record.flags?.some(f => f.status === 'open');
    
    if (hasOpenFlags && !confirmOpen) {
      setConfirmOpen(true);
      return;
    }

    setAcknowledging(true);
    try {
      await employeeAPI.acknowledgeRecord(id);
      toast.success('Record acknowledged!');
      navigate('/employee/records');
    } catch (error) {
      toast.error('Failed to acknowledge record');
    } finally {
      setAcknowledging(false);
      setConfirmOpen(false);
    }
  };

  const renderFieldWithFlag = (fieldName, label, content) => {
    const existingFlag = flaggedFields[fieldName];
    
    return (
      <div className="relative group">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold text-[#0A0F1E] mb-3">{label}</h3>
          <div className="flex items-center gap-2">
            {existingFlag && (
              <span className={`px-2 py-1 rounded-full text-xs ${
                existingFlag.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {existingFlag.status === 'resolved' ? 'Resolved' : 'Flagged'}
              </span>
            )}
            {!existingFlag && (
              <button
                onClick={() => setFlagField(fieldName)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-[#1B5AFF]/10 text-[#5B6472] hover:text-[#1B5AFF]"
                title="Flag this field"
              >
                <Flag className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className={`${existingFlag ? 'border-l-2 border-red-500/50 pl-4' : ''}`}>
          {content}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!record) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A0F1E]">Record not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-8" data-testid="employee-record-review">
        {/* Back Link */}
        <Link 
          to="/employee/records" 
          className="inline-flex items-center gap-2 text-[#5B6472] hover:text-[#0A0F1E] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Records
        </Link>

        {/* Review Banner */}
        <Card className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium">Review Required</p>
              <p className="text-amber-400/80 text-sm mt-1">
                Your employer has completed this record. Review it carefully. 
                You can flag any field you'd like to discuss before acknowledging.
              </p>
            </div>
          </div>
        </Card>

        {/* Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            {record.employer_logo ? (
              <img src={record.employer_logo} alt="" className="w-14 h-14 rounded-xl object-cover border border-[#E8EDF5]" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                <Building className="w-5 h-5 text-[#5B6472]" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-[#0A0F1E]">{record.job_title}</h1>
              <p className="text-[#5B6472]">{record.employer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#5B6472]">
            <Calendar className="w-4 h-4" />
            {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
          </div>
        </Card>

        {/* Skills */}
        {record.skills?.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            {renderFieldWithFlag('skills', 'Skills Demonstrated', (
              <div className="flex flex-wrap gap-2">
                {record.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-sm border border-[#E8EDF5]">
                    {skill}
                  </span>
                ))}
              </div>
            ))}
          </Card>
        )}

        {/* Strengths */}
        {record.strengths && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            {renderFieldWithFlag('strengths', 'Top Strengths', (
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.strengths}</p>
            ))}
          </Card>
        )}

        {/* Growth Areas */}
        {record.growth_areas && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            {renderFieldWithFlag('growth_areas', 'Areas of Growth', (
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{record.growth_areas}</p>
            ))}
          </Card>
        )}

        {/* Rehire Eligibility */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          {renderFieldWithFlag('rehire_eligibility', 'Rehire Eligibility', (
            <div>
              <span className={`inline-flex px-4 py-2 rounded-xl text-sm font-medium ${
                record.rehire_eligibility === 'yes' ? 'bg-green-500/20 text-green-400' :
                record.rehire_eligibility === 'conditional' ? 'bg-amber-500/20 text-amber-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {record.rehire_eligibility === 'yes' ? 'Yes' : 
                 record.rehire_eligibility === 'conditional' ? 'Conditional' : 'No'}
              </span>
              {record.rehire_condition && (
                <p className="text-[#5B6472] mt-3 text-sm">{record.rehire_condition}</p>
              )}
            </div>
          ))}
        </Card>

        {/* Rating */}
        {record.rating && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            {renderFieldWithFlag('rating', 'Overall Rating', (
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-8 h-8 ${i < record.rating ? 'fill-[#1B5AFF] text-[#1B5AFF]' : 'text-[#5B6472]'}`} 
                  />
                ))}
              </div>
            ))}
          </Card>
        )}

        {/* Public Note */}
        {record.public_note && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            {renderFieldWithFlag('public_note', 'Public Note', (
              <p className="text-[#0A0F1E]">{record.public_note}</p>
            ))}
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4">
          <Link to="/employee/records">
            <Button variant="outline" className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl">
              Back to Records
            </Button>
          </Link>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow"
                  data-testid="acknowledge-record-btn"
                >
                  {acknowledging ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Acknowledging...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Acknowledge Record
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] max-w-xs">
                <p className="text-sm">
                  Acknowledging means you've reviewed this record and had the opportunity to respond. 
                  Open flags remain visible.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Flag Dialog */}
        <Dialog open={!!flagField} onOpenChange={() => { setFlagField(null); setFlagMessage(''); }}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle>Flag Field: {flagField?.replace(/_/g, ' ')}</DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                What would you like to discuss about this field?
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={flagMessage}
              onChange={(e) => setFlagMessage(e.target.value.slice(0, 300))}
              placeholder="Explain your concern..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
            />
            <div className="flex justify-end text-[#5B6472] text-xs">
              {flagMessage.length}/300
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setFlagField(null); setFlagMessage(''); }} className="border-[#E8EDF5] text-[#0A0F1E]">
                Cancel
              </Button>
              <Button onClick={handleFlag} className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]">
                <Flag className="mr-2 h-4 w-4" />
                Submit Flag
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Dialog for Open Flags */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle>Open Flags Remaining</DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                You have open flags on this record. Acknowledging will publish it with those flags still marked as unresolved. Are you sure?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-[#E8EDF5] text-[#0A0F1E]">
                Go Back
              </Button>
              <Button onClick={handleAcknowledge} disabled={acknowledging} className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]">
                {acknowledging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Confirm Acknowledge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RecordReview;
